import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Button, Input, Modal, Field, FieldLabel, FieldError, Checkbox } from '../ui';
import { Lesson } from '@court-sync/shared';
import { CalendarClock, Mail, Bell, MessageSquare } from 'lucide-react';
import { formatTime } from '../../utils/timeFormat';

export interface OccurrenceDateOption {
  date: string; // YYYY-MM-DD
  label: string;
}

interface PostponeLessonModalProps {
  isOpen: boolean;
  lesson: Lesson | null;
  /** When provided, shows a date picker step first. When null, uses originalDate directly. */
  originalDate: string | null;
  /** Optional list of occurrence dates to choose from (for lesson management flow) */
  occurrenceDates?: OccurrenceDateOption[];
  /** When true, academy has SMS enabled on a tier that includes transactional SMS. */
  smsAvailable?: boolean;
  isLoading?: boolean;
  stackLevel?: 'default' | 'elevated';
  onCancel: () => void;
  onConfirm: (
    originalDate: string,
    newDate: string,
    newStartTime: string,
    newEndTime: string,
    reason: string,
    notifyStudents: boolean,
    notifySms: boolean
  ) => Promise<void>;
}

/** Format date YYYY-MM-DD to readable string */
function formatDateDisplay(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatPostponeSubmitError(
  message: string,
  context: { lessonName: string; originalDate: string; newDate: string }
): string {
  const lower = message.toLowerCase();
  if (lower.includes('session already exists')) {
    return `Cannot postpone "${context.lessonName}" to ${formatDateDisplay(context.newDate)} — another session for this lesson is already scheduled on that date. Choose a different date or remove the existing session first.`;
  }
  if (lower.includes('already cancelled or postponed')) {
    return `The ${formatDateDisplay(context.originalDate)} session is already cancelled or postponed and cannot be moved again.`;
  }
  if (lower.includes('cannot be in the past')) {
    return `The new date (${formatDateDisplay(context.newDate)}) must be today or later.`;
  }
  return message;
}

export const PostponeLessonModal: React.FC<PostponeLessonModalProps> = ({
  isOpen,
  lesson,
  originalDate,
  occurrenceDates,
  isLoading = false,
  smsAvailable = false,
  stackLevel = 'default',
  onCancel,
  onConfirm,
}) => {
  const [selectedOriginalDate, setSelectedOriginalDate] = useState<string | null>(null);
  const [newDate, setNewDate] = useState('');
  const [newStartTime, setNewStartTime] = useState('');
  const [newEndTime, setNewEndTime] = useState('');
  const [reason, setReason] = useState('');
  const [notifyStudents, setNotifyStudents] = useState(true);
  const [notifySms, setNotifySms] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'select-date' | 'form' | 'confirm'>(
    occurrenceDates && occurrenceDates.length > 0 ? 'select-date' : 'form'
  );

  const effectiveOriginalDate = selectedOriginalDate || originalDate;

  useEffect(() => {
    if (isOpen) {
      setSelectedOriginalDate(null);
      setStep(occurrenceDates && occurrenceDates.length > 0 ? 'select-date' : 'form');
      if (lesson?.startTime && lesson?.endTime) {
        setNewStartTime(lesson.startTime);
        setNewEndTime(lesson.endTime);
      }
    }
  }, [isOpen, occurrenceDates, lesson?.startTime, lesson?.endTime]);

  const handleProceedToConfirm = () => {
    if (!newDate) {
      setError('Please select a new date');
      return;
    }
    if (!newStartTime || !newEndTime) {
      setError('Please provide new start and end times');
      return;
    }
    if (!reason.trim()) {
      setError('Please provide a reason for postponement');
      return;
    }
    setError('');
    setStep('confirm');
  };

  const handleConfirmSubmit = async () => {
    if (!effectiveOriginalDate) return;
    try {
      setSubmitting(true);
      setError('');
      await onConfirm(
        effectiveOriginalDate,
        newDate,
        newStartTime,
        newEndTime,
        reason,
        notifyStudents,
        smsAvailable ? notifySms : false
      );
      handleClose();
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : 'Failed to postpone lesson';
      toast.error(
        formatPostponeSubmitError(rawMessage, {
          lessonName: lesson.name,
          originalDate: effectiveOriginalDate,
          newDate,
        }),
        { duration: 6000 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedOriginalDate(null);
    setNewDate('');
    setNewStartTime('');
    setNewEndTime('');
    setReason('');
    setNotifyStudents(true);
    setNotifySms(true);
    setError('');
    setStep(occurrenceDates && occurrenceDates.length > 0 ? 'select-date' : 'form');
    onCancel();
  };

  const handleBackToForm = () => {
    setStep('form');
    setError('');
  };

  const handleSelectDateConfirm = () => {
    if (selectedOriginalDate) {
      setStep('form');
    }
  };

  if (!isOpen || !lesson) {
    return null;
  }

  const showSelectDateStep = occurrenceDates && occurrenceDates.length > 0 && step === 'select-date';
  const showFormStep = step === 'form' && effectiveOriginalDate;
  const showConfirmStep = step === 'confirm';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Postpone Lesson Session" maxWidth="sm" stackLevel={stackLevel}>
      <div>
        {showSelectDateStep ? (
          <>
            <div className="space-y-4 mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Select which lesson date you want to postpone:
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {occurrenceDates!.map((opt) => (
                  <button
                    key={opt.date}
                    type="button"
                    onClick={() => setSelectedOriginalDate(opt.date)}
                    className={`w-full text-left px-4 py-2 rounded-lg border transition-colors ${
                      selectedOriginalDate === opt.date
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-600'
                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    <span className="font-medium">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleSelectDateConfirm} disabled={!selectedOriginalDate}>
                Continue
              </Button>
            </div>
          </>
        ) : showFormStep ? (
          <>
            <div className="space-y-4 mb-6">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Lesson:</strong> {lesson.name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Original Date:</strong> {effectiveOriginalDate}
                </p>
              </div>

              <Field>
                <FieldLabel>New Date</FieldLabel>
                <Input
                  type="date"
                  value={newDate}
                  onChange={(e) => {
                    setNewDate(e.target.value);
                    if (error) setError('');
                  }}
                  disabled={submitting || isLoading}
                  min={new Date().toISOString().split('T')[0]}
                />
                {error && <FieldError>{error}</FieldError>}
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field>
                  <FieldLabel>Start Time</FieldLabel>
                  <Input
                    type="time"
                    value={newStartTime}
                    onChange={(e) => {
                      setNewStartTime(e.target.value);
                      if (error) setError('');
                    }}
                    disabled={submitting || isLoading}
                  />
                </Field>
                <Field>
                  <FieldLabel>End Time</FieldLabel>
                  <Input
                    type="time"
                    value={newEndTime}
                    onChange={(e) => {
                      setNewEndTime(e.target.value);
                      if (error) setError('');
                    }}
                    disabled={submitting || isLoading}
                  />
                </Field>
              </div>

              <Field>
                <FieldLabel>Reason for Postponement</FieldLabel>
                <textarea
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="E.g., Weather issues, Coach unavailable"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800"
                  rows={3}
                  disabled={submitting || isLoading}
                />
              </Field>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="notify-students"
                  checked={notifyStudents}
                  onCheckedChange={(checked) =>
                    setNotifyStudents(typeof checked === 'boolean' ? checked : false)
                  }
                  disabled={submitting || isLoading}
                />
                <label
                  htmlFor="notify-students"
                  className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                  Notify students/guardians about reschedule (email + in-app)
                </label>
              </div>

              {smsAvailable && (
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="notify-sms-postpone"
                    checked={notifySms}
                    onCheckedChange={(checked) =>
                      setNotifySms(typeof checked === 'boolean' ? checked : false)
                    }
                    disabled={submitting || isLoading}
                  />
                  <label
                    htmlFor="notify-sms-postpone"
                    className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                  >
                    Send SMS to opted-in guardians and students
                  </label>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={submitting || isLoading}
              >
                Close
              </Button>
              <Button
                onClick={handleProceedToConfirm}
                disabled={submitting || isLoading}
              >
                Continue to Confirm
              </Button>
            </div>
          </>
        ) : showConfirmStep ? (
          <>
            <div className="space-y-4 mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-blue-900 dark:text-blue-100 text-sm">
                <strong>Please confirm:</strong>{' '}
                {notifyStudents || (smsAvailable && notifySms)
                  ? 'This will move the lesson to the new date and notify enrolled students/guardians.'
                  : 'This will move the lesson to the new date without sending notifications.'}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <CalendarClock className="h-4 w-4 text-gray-500" />
                  <span>
                    <strong>{lesson.name}</strong> — from {effectiveOriginalDate} to {formatDateDisplay(newDate)} at {formatTime(newStartTime)}–{formatTime(newEndTime)}
                  </span>
                </div>
                <div className="text-gray-600 dark:text-gray-400 ml-6">
                  Reason: {reason}
                </div>
                {notifyStudents && (
                  <div className="flex items-center gap-2 mt-2 text-gray-600 dark:text-gray-400">
                    <Mail className="h-4 w-4" />
                    <Bell className="h-4 w-4" />
                    <span>Students/guardians will receive email and in-app notification</span>
                  </div>
                )}
                {smsAvailable && notifySms && (
                  <div className="flex items-center gap-2 mt-2 text-gray-600 dark:text-gray-400">
                    <MessageSquare className="h-4 w-4" />
                    <span>Opted-in guardians and students will receive an SMS</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={handleBackToForm}
                disabled={submitting || isLoading}
              >
                Back
              </Button>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={submitting || isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSubmit}
                disabled={submitting || isLoading}
                isLoading={submitting || isLoading}
              >
                Confirm Postpone
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
};

export default PostponeLessonModal;
